import { DataExplorationChart } from "reducers/configurableChartReducer";
import { useGetAlertFromParam } from "hooks/alerts/useGetAlertFromParam";
import { useGetRule } from "hooks/rules/useGetRule";
import { useGetMinMaxTagsMachines } from "hooks/data/useGetMinMaxTagsMachines";
import { useContext, useMemo } from "react";
import { SeverityTypeContext } from "contexts/SeverityTypeContext";
import { v4 as createUUID } from "uuid";
import { RuleLevel } from "interfaces/rule";
import { getRuleLevelPerSeverityType } from "components/AlertsManagement/Chart/getRuleLevelPerSeverityType";
import { LineSeries, RawDataSource } from "interfaces/chart";
import { getSeverityColour } from "core/utils/severityTypes";
import { getThresholdTypeName } from "components/AlertsManagement/Chart/getSeriesFromRuleLevels";
import { generateNewChart } from "core/data-exploration-v2-utils";

/** Creates a series for the specified rule level, so that a threshold can be displayed on the chart. */
const createThresholdSeries = (
  parentId: string,
  severityTypes: any[],
  ruleLevel: RuleLevel,
  minDate: Date,
  maxDate: Date,
  xAxisId: string,
  yAxisId: string,
  colorIndex: number
): LineSeries<RawDataSource> => {
  // TODO: tidy this up and use threshold colour
  // TODO: remove "as any" casts

  const severityType = (severityTypes as any).find(
    (x: any) => x.id === ruleLevel.rule_level_type_id
  );

  let thresholdColour = getSeverityColour(severityType.colour);

  const thresholdTypeName = getThresholdTypeName(
    ruleLevel.site_id,
    ruleLevel.machine_id
  );

  const thresholdName = `${severityType.name ?? ""}: ${thresholdTypeName}`;

  return {
    color: thresholdColour,
    colorIndex: colorIndex,
    dataSource: {
      type: "raw",
      args: {
        data: [
          [minDate.getTime(), ruleLevel.level],
          [maxDate.getTime(), ruleLevel.level]
        ]
      }
    },
    id: createUUID(),
    parentId: parentId,
    pointsEnabled: false,
    seriesType: "line",
    title: thresholdName,
    xAxisId,
    yAxisId
  };
};

/** Creates an alert chart using the alert id from the url params if present.
 * Otherwise, returns null. */
export const useGetAlertChartFromParam = (): {
  loading: boolean;
  alertChart: DataExplorationChart | null;
} => {
  const { loading: alertLoading, alert } = useGetAlertFromParam();
  const { loading: ruleLoading, rule } = useGetRule(alert?.rule_id);
  const machineIds = alert?.machine_id ? [alert.machine_id] : [];
  const tagIds = alert?.tag_id ? [alert.tag_id] : [];
  const { loading: rangeLoading, range } = useGetMinMaxTagsMachines(
    machineIds,
    tagIds
  );
  const { loading: severityTypesLoading, severityTypes } = useContext(
    SeverityTypeContext
  );
  const loading =
    alertLoading || ruleLoading || rangeLoading || severityTypesLoading;

  const alertChart: DataExplorationChart | null = useMemo(() => {
    if (!loading && alert) {
      const alertSeriesId = createUUID();

      const chart = generateNewChart(
        alertSeriesId,
        alert.machine_id,
        alert.machine_name,
        alert.tag_id,
        alert.tag_name
      );

      if (rule && range) {
        const relevantRuleLevels: RuleLevel[] = getRuleLevelPerSeverityType(
          rule.rule_levels,
          alert.machine_id,
          severityTypes
        );

        relevantRuleLevels.forEach(ruleLevel => {
          const thresholdSeries = createThresholdSeries(
            alertSeriesId,
            severityTypes,
            ruleLevel,
            new Date(range.min_date),
            new Date(range.max_date),
            chart.series[0].xAxisId,
            chart.series[0].yAxisId,
            // Color index should be same as the parent, so that it doesn't take up a new color
            chart.series[0].colorIndex
          );

          return chart.series.push(thresholdSeries);
        });
      }

      return chart;
    }

    return null;
  }, [alert, loading, range, rule, severityTypes]);

  return { loading, alertChart };
};
